import type { ReactNode } from "react";

import { BotanicalPattern } from "@/features/auth/presentation/components/BotanicalPattern";
import { BrandMark } from "@/features/auth/presentation/components/BrandMark";
import { ENV } from "@/shared/lib/env";
import { cn } from "@/shared/lib/utils";

interface AuthLayoutProps {
  /** Slot del form (login, MFA, session expired, etc.). */
  children: ReactNode;
  /** Título grande del panel brand. */
  visualTitle?: string;
  /** Subtítulo del panel brand. */
  visualSubtitle?: string;
  /** Pie opcional del form panel. Default: copyright. */
  footer?: ReactNode;
}

/**
 * Layout editorial compartido para pantallas de auth. Split-screen:
 *
 *  Panel brand (lg+)
 *   - Fondo arya-forest con noise sutil (grain) para evitar flat-color.
 *   - BotanicalPattern en opacidad baja, mascarado con fade-bottom.
 *   - Display Fraunces grande como statement de marca; mono micro para
 *     metadatos (env, tagline) abajo.
 *   - Jerarquía: BrandMark arriba, headline centrada-abajo, meta pegada
 *     al pie. Mucho whitespace vertical.
 *
 *  Panel form
 *   - BG background limpio. Card centrada con padding generoso.
 *   - Mobile: colapsa a single column; brand chip arriba.
 */
export function AuthLayout({
  children,
  visualTitle = "Cuidamos el camino con la misma atención con la que lo diseñamos.",
  visualSubtitle = "Backoffice administrativo del ecosistema Arya — consejos, acompañamiento y auditoría.",
  footer,
}: AuthLayoutProps) {
  const envLabel =
    ENV.VITE_ENVIRONMENT === "production"
      ? "Producción"
      : ENV.VITE_ENVIRONMENT === "staging"
        ? "Staging"
        : "Desarrollo";

  return (
    <div className="relative grid min-h-svh bg-background lg:grid-cols-[1.1fr_1fr]">
      {/* ─────────────── Panel brand ─────────────── */}
      <aside
        aria-hidden={false}
        className="relative hidden overflow-hidden bg-arya-forest text-arya-cream lg:flex lg:flex-col"
      >
        {/* Base fill con subtle gradient para dar profundidad sin blobs. */}
        <div
          aria-hidden
          className={cn(
            "absolute inset-0",
            "bg-[radial-gradient(ellipse_at_top_left,theme(colors.arya.leaf)_0%,transparent_55%),linear-gradient(180deg,#1E5E3A_0%,#2E7A55_60%,#235F43_100%)]",
          )}
        />
        {/* Grain overlay — organiza el flat. */}
        <div aria-hidden className="bg-grain absolute inset-0 opacity-[0.18] mix-blend-overlay" />
        {/* Pattern botánico — mano del cream, muy bajo contraste. */}
        <div aria-hidden className="mask-fade-b absolute inset-0 text-arya-cream/[0.09]">
          <BotanicalPattern className="h-full w-full" />
        </div>
        {/* Luz suave superior-izquierda para dirigir ojo al headline. */}
        <div
          aria-hidden
          className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-arya-mist/15 blur-3xl"
        />

        {/* Contenido */}
        <div className="relative z-10 flex h-full flex-col p-12 xl:p-14">
          {/* Top: BrandMark + env chip */}
          <div className="flex animate-fade-in-up items-center justify-between">
            <BrandMark tone="onDark" size="md" />
            <span
              className={cn(
                "rounded-full border border-arya-cream/15 bg-arya-cream/[0.06] px-3 py-1 backdrop-blur-sm",
                "font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-arya-cream/75",
              )}
            >
              {envLabel}
            </span>
          </div>

          {/* Spacer que empuja el headline hacia el centro-inferior. */}
          <div className="flex-1" />

          {/* Headline editorial */}
          <div className="max-w-xl animate-fade-in-up" style={{ animationDelay: "80ms" }}>
            <h2
              className={cn(
                "font-display font-light tracking-display-tighter text-arya-cream",
                "text-[2.75rem] leading-[1.02] xl:text-[3.25rem]",
              )}
            >
              {visualTitle}
            </h2>
            <p className="mt-6 max-w-md text-[0.9375rem] leading-relaxed text-arya-cream/70">
              {visualSubtitle}
            </p>
          </div>

          {/* Pie: hairline + tagline + versioning sutil */}
          <div
            className="mt-14 flex animate-fade-in-up items-center gap-4"
            style={{ animationDelay: "160ms" }}
          >
            <div className="h-px flex-1 bg-arya-cream/15" />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-arya-cream/50">
              Hecho con cuidado
            </span>
            <div className="h-px flex-1 bg-arya-cream/15" />
          </div>
        </div>
      </aside>

      {/* ─────────────── Panel form ─────────────── */}
      <main className="relative flex flex-col">
        {/* Mobile brand chip (panel brand oculto en <lg) */}
        <div className="flex items-center justify-between border-b border-border/60 px-6 py-4 lg:hidden">
          <BrandMark tone="onLight" size="sm" />
          <span className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
            {envLabel}
          </span>
        </div>

        <div className="flex flex-1 items-center justify-center p-6 py-10 lg:p-12">
          <div
            className="w-full max-w-[26rem] animate-fade-in-up"
            style={{ animationDelay: "40ms" }}
          >
            {children}
          </div>
        </div>

        <footer className="px-6 pb-6 text-xs text-muted-foreground lg:px-12">
          {footer ?? (
            <div className="flex items-center justify-between gap-4">
              <span>© {new Date().getFullYear()} Arya</span>
              <a
                href="mailto:soporte@arya.pe"
                className="font-medium text-foreground/70 underline-offset-4 transition-colors hover:text-foreground hover:underline"
              >
                soporte@arya.pe
              </a>
            </div>
          )}
        </footer>
      </main>
    </div>
  );
}
